import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const heroSlides = [
  {
    src: 'https://readdy.ai/api/search-image?query=dark%20moody%20premium%20butcher%20shop%20interior%20artisan%20meat%20counter%20rustic%20wood%20warm%20ambient%20lighting%20professional%20photography%20high%20contrast%20deep%20reds%20browns%20cinematic%20wide%20angle&width=1440&height=800&seq=hero-slide-1&orientation=landscape',
    alt: 'Interior de carnicería premium con mostrador artesanal de madera rústica',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=premium%20raw%20beef%20cuts%20assortment%20ribeye%20sirloin%20short%20ribs%20displayed%20on%20butcher%20block%20dark%20moody%20professional%20food%20photography%20warm%20lighting%20rustic%20style&width=1440&height=800&seq=hero-slide-2&orientation=landscape',
    alt: 'Variedad de cortes premium de res exhibidos en tabla de carnicero',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=fresh%20raw%20meat%20display%20case%20butcher%20shop%20glass%20counter%20pork%20chicken%20beef%20cuts%20professional%20photography%20warm%20ambient%20lighting%20rustic%20wooden%20details&width=1440&height=800&seq=hero-slide-3&orientation=landscape',
    alt: 'Vitrina de carnicería con cortes frescos de res cerdo y pollo',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=butcher%20hands%20cutting%20fresh%20meat%20on%20wooden%20block%20professional%20artisan%20meat%20shop%20dark%20background%20warm%20lighting%20high%20quality%20photography%20close%20up&width=1440&height=800&seq=hero-slide-4&orientation=landscape',
    alt: 'Manos de carnicero cortando carne fresca en tabla de madera artesanal',
  },
];

export default function Hero() {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollReveal(0.1);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  useEffect(() => {
    if (isHovered) return;
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isHovered, nextSlide]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <section
      id="inicio"
      className="relative min-h-[600px] md:min-h-[720px] flex items-center overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background Image Slider */}
      <div className="absolute inset-0">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
            style={{ opacity: index === currentSlide ? 1 : 0 }}
          >
            <img
              src={slide.src}
              alt={slide.alt}
              className="w-full h-full object-cover object-center"
            />
          </div>
        ))}
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/80 via-brand-dark/60 to-brand-dark/30" />
        {/* Bottom vignette for dots readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/40 via-transparent to-transparent" />
      </div>

      {/* Slide Indicators / Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              index === currentSlide
                ? 'w-8 h-2 bg-brand-gold'
                : 'w-2 h-2 bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`Ver slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Slide Counter */}
      <div className="absolute bottom-6 right-4 md:right-8 z-20">
        <span className="text-white/60 text-xs font-medium tracking-wider">
          <span className="text-brand-gold font-bold">{String(currentSlide + 1).padStart(2, '0')}</span>
          {' / '}
          {String(heroSlides.length).padStart(2, '0')}
        </span>
      </div>

      {/* Content */}
      <div
        ref={ref}
        className={`relative z-10 w-full px-4 md:px-8 lg:px-12 pt-20 transition-all duration-1000 ${
          isVisible
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="max-w-3xl mx-auto md:mx-0 text-center md:text-left">
          <div className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-brand-gold/20 backdrop-blur-sm rounded-full mb-6">
            <i className="ri-award-line text-brand-gold" />
            <span className="text-brand-gold text-sm font-medium">
              {t('hero_badge')}
            </span>
          </div>

          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {t('hero_title_line1')}
            <br />
            <span className="text-brand-gold">{t('hero_title_line2')}</span>
          </h1>

          <p className="text-white/80 text-base md:text-lg max-w-xl mx-auto md:mx-0 mb-8 leading-relaxed">
            {t('hero_subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4">
            <a
              href="#catalogo"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-gold text-brand-dark font-semibold rounded-full hover:bg-brand-gold/90 transition-all duration-300 hover:scale-[1.02] whitespace-nowrap"
            >
              <span>{t('hero_cta_explore')}</span>
              <i className="ri-arrow-right-line" />
            </a>
            <a
              href="#nosotros"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white/10 transition-all duration-300 whitespace-nowrap"
            >
              <span>{t('hero_cta_story')}</span>
            </a>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap justify-center md:justify-start gap-8 mt-12 pt-8 border-t border-white/20">
            <div>
              <div className="text-2xl md:text-3xl font-bold text-brand-gold">
                8+
              </div>
              <div className="text-white/60 text-sm mt-1">
                {t('hero_stat_suppliers')}
              </div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-brand-gold">
                25+
              </div>
              <div className="text-white/60 text-sm mt-1">
                {t('hero_stat_cuts')}
              </div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-brand-gold">
                3K+
              </div>
              <div className="text-white/60 text-sm mt-1">
                {t('hero_stat_clients')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}